<template>
  <RightData :right-data="rightData" :rightState="rightState" :has-seria="!!seriesDetails.serialsId" @next="getRecommend" />
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue'
import { isArray } from 'lodash-es'
import { useNetwork, useUrlSearchParams } from '@vueuse/core'
import { useStore } from '@gx-vuex'
import { getSerialsList, getDetailsSuggest } from '@/services/picDetails'
import {
  hanldeNewsDetails,
  getFileSuffix,
  getUUID,
  isMobileDevice,
  getVideoCoverPicture,
  getBlobUrl,
  dataURLtoBlob
} from '@/utils/util'
import { typeViteEnv, isBuild } from '@/utils/env'
import { isIOS } from '@vueuse/shared'
import global from '@/common/global'
import RightData from './RightData'

const { isOnline, effectiveType } = useNetwork()
const paramsUrls = useUrlSearchParams()

const seriesDetails = reactive<{
  serialsId?: string;
  serialsName?: string;
}>({})

const pageState = reactive({
  pageNum: 1,
  pageSize: 5
})

const rightState = reactive({
  init: false,
  loading: false,
  hasMore: true
})

const firstPlay = ref(false)

const rightData = ref<NewsItem[]>([])

const isFullVideo = ref(false)

const contentId = document.body.getAttribute('data-id')
const objectType = document.querySelector('body').getAttribute('data-objectType')
const channelIds = document.querySelector('body').getAttribute('data-channelId')

const requestTime = ref()
const store = useStore()

const videoPlayer = ref()

const publicImageUrl = isBuild() ? typeViteEnv('VITE_OSS_BUILD_URL') + 'resource/image' : '/src/assets/image'

function dispose() {
  if (videoPlayer.value) videoPlayer.value?.destroy(true)
  videoPlayer.value = null
}

watch(() => effectiveType.value, (val) => {
  if (val === '2g') {
    dispose()
    document.querySelector('#live-message').innerHTML = `<span>Please check your network connection and <a href="${location.href}">try again!</a></span>`
  } else {
    if (videoPlayer.value) videoPlayer.value?.destroy(true)
    initPlayer()
    if (isFullVideo.value) {
      document.querySelector('.video-content .left').setAttribute('class', 'left full')
      // @ts-ignore
      document.querySelector('#videoPlayerWrapper').style.height = '600px'
    }
  }
})

watch(() => isOnline.value, (val) => {
  if (!val) {
    dispose()
    document.querySelector('#live-message').innerHTML = `<span>Please check your network connection and <a href="${location.href}">try again!</a></span>`
  } else {
    if (videoPlayer.value) videoPlayer.value?.destroy(true)
    initPlayer()
    if (isFullVideo.value) {
      document.querySelector('.video-content .left').setAttribute('class', 'left full')
      // @ts-ignore
      document.querySelector('#videoPlayerWrapper').style.height = '600px'
    }
  }
})

async function initPlayer() {
  if (isOnline.value) {
    try {
      // @ts-ignore
      if (slow === 0) {
        // @ts-ignore
        slow = 3
      }
      document.querySelector('#live-message').innerHTML = ``
      
      const playerDom = document.getElementById('videoPlayer')
      
      if (playerDom && playerDom.getAttribute('data-src')) {
        const url = playerDom.getAttribute('data-src')
        const dataCover = playerDom.getAttribute('data-cover')
        
        if (getFileSuffix(url) == 'm3u8') {
          // @ts-ignore
          videoPlayer.value = new Artplayer({
            container: '#videoPlayerWrapper',
            lang: 'en',
            url,
            moreVideoAttr: {
              'webkit-playsinline': true,
              preload: 'preload',
              'x-webkit-airplay': 'true',
              playsInline: true
            },
            fullscreenWeb: true,
            icons: {
              play: `<img style="width: 22px;height: 22px;" src="${publicImageUrl}/player/video_puse.svg" alt="">`,
              pause: `<img style="width: 22px;height: 22px;" src="${publicImageUrl}/player/video_play.svg" alt="">`,
              state: `<img style="width: 60px;height: 60px;" src="${publicImageUrl}/puse.svg" alt="">`,
              volume: `<img style="width: 22px;height: 22px;" src="${publicImageUrl}/iconSound.svg" alt="">`,
              volumeClose: `<img style="width: 22px;height: 22px;" src="${publicImageUrl}/iconSoundCLose.svg" alt="">`,
              fullscreenWebOn: `<img style="width: 22px;height: 22px;" src="${publicImageUrl}/iconFullScreen.svg" alt="">`,
              fullscreenWebOff: `<img style="width: 22px;height: 22px;" src="${publicImageUrl}/iconExFullScreen.svg" alt="">`,
              fullscreenOn: `<img style="width: 22px;height: 22px;" src="${publicImageUrl}/iconFullScreen.svg" alt="">`,
              fullscreenOff: `<img style="width: 22px;height: 22px;" src="${publicImageUrl}/iconExFullScreen.svg" alt="">`
            },
            customType: {
              m3u8: function playM3u8(video, url, art) {
                // @ts-ignore
                if (Hls.isSupported()) {
                  if (art.hls) art.hls.destroy()
                  // @ts-ignore
                  const hls = new Hls()
                  hls.loadSource(url)
                  hls.attachMedia(video)
                  // @ts-ignore
                  art.hls.on(Hls.Events.ERROR, (e, data) => {
                    if (data.fatal) {
                      store.point.videoError('error')
                      
                      dispose()
                      // @ts-ignore
                      document.querySelector('#live-message').style.zIndex = 10
                      document.querySelector('#live-message').innerHTML = `<span>Please check your network connection and <a href="${location.href}">try again!</a></span>`
                    }
                  })
                  art.hls = hls
                  art.on('destroy', () => hls.destroy())
                } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
                  video.src = url
                } else {
                  art.notice.show = 'Unsupported playback format: m3u8'
                }
              }
            }
          })
        } else {
          let cover = dataCover || ''
          if (!isMobileDevice() && !cover) {
            const response = await getVideoCoverPicture({
              url
            })
            if (response) cover = getBlobUrl(dataURLtoBlob(response))
          }
          // @ts-ignore
          videoPlayer.value = new Artplayer({
            container: '#videoPlayerWrapper',
            lang: 'en',
            url,
            poster: cover,
            moreVideoAttr: {
              'webkit-playsinline': true,
              preload: 'preload',
              'x-webkit-airplay': 'true',
              playsInline: true
            },
            fullscreenWeb: true,
            icons: {
              play: `<img style="width: 22px;height: 22px;" src="${publicImageUrl}/player/video_puse.svg" alt="">`,
              pause: `<img style="width: 22px;height: 22px;" src="${publicImageUrl}/player/video_play.svg" alt="">`,
              state: `<img style="width: 60px;height: 60px;" src="${publicImageUrl}/puse.svg" alt="">`,
              volume: `<img style="width: 22px;height: 22px;" src="${publicImageUrl}/iconSound.svg" alt="">`,
              volumeClose: `<img style="width: 22px;height: 22px;" src="${publicImageUrl}/iconSoundCLose.svg" alt="">`,
              fullscreenWebOn: `<img style="width: 22px;height: 22px;" src="${publicImageUrl}/iconFullScreen.svg" alt="">`,
              fullscreenWebOff: `<img style="width: 22px;height: 22px;" src="${publicImageUrl}/iconExFullScreen.svg" alt="">`,
              fullscreenOn: `<img style="width: 22px;height: 22px;" src="${publicImageUrl}/iconFullScreen.svg" alt="">`,
              fullscreenOff: `<img style="width: 22px;height: 22px;" src="${publicImageUrl}/iconExFullScreen.svg" alt="">`
            }
          })
        }
        
        if (videoPlayer.value) {
          if (isIOS) {
            setTimeout(() => {
              videoPlayer.value.loading.show = false
            }, 100)
          }
        }
        
        videoPlayer.value.on('play', () => {
          if (!firstPlay.value) {
            firstPlay.value = true
            store.point.videoPlay({
              duration: videoPlayer.value.duration
            })
          }
        })
        
        videoPlayer.value.on('video:ended', () => {
          store.point.videoEnd({
            currentTime: videoPlayer.value.currentTime,
            duration: videoPlayer.value.duration
          })
        })
        
        videoPlayer.value.on('video:error', (e) => {
          dispose()
          // @ts-ignore
          document.querySelector('#live-message').style.zIndex = 10
          document.querySelector('#live-message').innerHTML = `<span>Please check your network connection and <a href="${location.href}">try again!</a></span>`
          store.point.videoError(e.type)
          console.error('video error',e)
        })
        
        videoPlayer.value.on('fullscreenWeb', () => {
          store.point.nameClick('full_screen_click')
        })
      }
    } catch (e) {
      dispose()
      // @ts-ignore
      document.querySelector('#live-message').style.zIndex = 10
      document.querySelector('#live-message').innerHTML = `<span>Please check your network connection and <a href="${location.href}">try again!</a></span>`
    }
  } else {
    dispose()
    // @ts-ignore
    document.querySelector('#live-message').style.zIndex = 10
    document.querySelector('#live-message').innerHTML = `<span>Please check your network connection and <a href="${location.href}">try again!</a></span>`
  }
}

initPlayer()

// const hasNext = ref(false)

const getRecommend = async () => {
  if (rightState.loading || !rightState.hasMore) return
  rightState.loading = true
  let response = null
  if (!!seriesDetails.serialsId) {
    pageState.pageNum += 1
    response = await getSerialsList({
      pageNum: pageState.pageNum,
      serialsId: seriesDetails?.serialsId,
      requestTime: requestTime.value,
      pageSize: pageState.pageSize
    })
  } else {
    response = await getDetailsSuggest({
      url: '/recommend/en/c/detail',
      method: 'post',
      data: {
        returnCount: 5,
        channelIds: channelIds ? channelIds?.split(',') || [] : [],
        contentId: Number(contentId),
        contentType: Number(objectType),
        imei: getUUID('PeoplesDaily')
      }
    })
  }
  
  if (response) {
    const dataSource = (response.data?.list || (isArray(response.data) ? response.data : [])).map((item: NewsItem) => {
      if (!!seriesDetails.serialsId) {
        item.mainChannel = {} as MainChannel
        item.mainChannel.channelId = response.data.serialsId || ''
        item.mainChannel.channelName = response.data.serialsName || ''
      }
      
      store.point.contentListExposure(pointData(item))
      return hanldeNewsDetails(item)
    })
    
    rightData.value = rightData.value.concat(dataSource || [])
    
    if (!!seriesDetails.serialsId) {
      rightState.hasMore = rightData.value?.length < (response.data.totalCount || 0)
    } else {
      rightState.hasMore = false
    }
    
  }
  
  rightState.init = true
  rightState.loading = false
  
  if (rightData.value.length === 0) {
    if (isOnline.value) {
      isFullVideo.value = true
      document.querySelector('.video-content .left').setAttribute('class', 'left full')
      // @ts-ignore
      document.querySelector('#videoPlayerWrapper').style.height = '600px'
    }
  }
  
  initLoading()
}

const pointData = (record: any, trace?: boolean) => {
  return {
    pageId: record?.mainChannel?.pageId,
    newsType: record.objectType,
    newsId: record.objectId,
    appstyle: record.appStyle,
    link_url: record.linkUrl,
    live_type: !!record.liveInfo ? global.live_type_change[record.liveInfo?.liveState] || '' : '',
    live_stream_type: !!record.liveInfo ? record.liveInfo?.vrType == '1' ? '2' : '1' : '',
    live_mode: !!record.liveInfo ? record.liveInfo?.tplId == '6' ? '2' : '1' : '',
    vlive_id: !!record.liveInfo ? record.liveInfo?.vlive?.[0]?.vliveId || '' : '',
    vlive_name: !!record.liveInfo ? record.liveInfo?.vlive?.[0]?.name || '' : '',
    newsTitle: record.newsTitle,
    secondId: '',
    channelId: record?.mainChannel?.channelId,
    author_name: record.newsAuthor,
    summary_type: record.objectType == '5' ? record?.objectLevel == 14
      ? global.summary_type.normal_subject
      : record?.objectLevel == 16
        ? 'thematic_subject' : record?.objectType == 16 ? 'author_subject' : '' : '',
    summary_id: record.objectType == '5' ? record.objectId : '',
    trace,
    sceneId: trace ? record.sceneId : '',
    traceId: trace ? record.traceId : '',
    traceInfo: trace ? record.traceInfo : ''
  }
}

const initLoading = () => {
  document.querySelector('.video-loading').classList.add('none')
}

watch(() => store.global.initContentState, (val) => {
  if (val === 1) {
    document.getElementById('videoShare').setAttribute('class', 'share-block')
    store.point.contentExposure(Number(objectType))
    store.point.channelExposure(Number(objectType))
    seriesDetails.serialsId = store.global.details?.serials?.serialsId
    seriesDetails.serialsName = store.global.details?.serials?.serialsName
    if (paramsUrls.promote == 'true') {
      initLoading()
      if (isOnline.value) {
        isFullVideo.value = true
        document.querySelector('.video-content .left').setAttribute('class', 'left full')
        // @ts-ignore
        document.querySelector('#videoPlayerWrapper').style.height = '600px'
      }
    } else {
      getRecommend()
    }
  } else {
    if (isOnline.value) {
      initLoading()
      isFullVideo.value = true
      document.querySelector('.video-content .left').setAttribute('class', 'left full')
      // @ts-ignore
      document.querySelector('#videoPlayerWrapper').style.height = '600px'
    }
  }
})

onMounted(async () => {
  window.addEventListener('beforeunload', function () {
    if (videoPlayer.value) {
      store.point.videoEnd({
        currentTime: videoPlayer.value.currentTime,
        duration: videoPlayer.value.duration
      })
    }
  })
})
</script>

<style scoped lang="less">
.videoRightBox {
  width: 174px;
  height: 66px;
  word-wrap: break-word;
  font-size: 16px;
  color: #FFFFFF;
  line-height: 22px;
  font-family: DroidSerif-Bold-Regular, DroidSerif-Bold;
}

@import "./style";
</style>
